import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DropdownOption, FloatingTabs } from '@dbt-labs/sourdough';

import { Page } from '@/components/Page';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useDevelopmentContext } from '@/context/DevelopmentContextProvider';
import { useAdminProductionEnvironments } from '@/hooks/admin/environments';
import { useJobs } from '@/hooks/admin/jobs';
import { useProjects } from '@/hooks/admin/project';
import {
  EnvironmentSummary,
  useEnvironmentSummaries,
} from '@/hooks/discovery/environmentSummary';
import { useAllPublicModels } from '@/hooks/discovery/publicModels';
import { ResourceByEnvironmentParams } from '@/hooks/discovery/resource';
import { useQueryParamOption } from '@/hooks/queryParamOption';
import { SearchNode, useAccountLevelSearchResults } from '@/hooks/useFuzzySearch';
import { makeUniqueFilter } from '@/util/array';
import { ViewType, viewTypes } from '@/util/linkTypes';
import { buildTypePredicate } from '@/util/type';
import { ColumnsSidePanel } from './components/ColumnsSidePanel';
import { MeshProjectTable, ProjectEntry } from './components/MeshProjectTable';
import { MultiProjectLineageView } from './components/MultiProjectLineageView';
import { PublicModelsTable } from './components/PublicModelsTable';
import { INITIAL_PAGE_SIZE } from './project/resource/components/ResourceFilterTable';

const tabTypes = ['publicModels', 'projects'] as const;
export type TabType = (typeof tabTypes)[number];

const isTabType = buildTypePredicate(tabTypes);
const isViewType = buildTypePredicate(viewTypes);
const getTabType = (input: string | undefined | null): TabType | null => {
  return isTabType(input) ? input : null;
};
const getViewType = (input: string | undefined | null): ViewType | null => {
  return isViewType(input) ? input : null;
};

export const MultiProjectPage = () => {
  const [searchParams] = useSearchParams();
  const search = searchParams.get('search') || '';
  const devData = useDevelopmentContext();

  // TODO: how do I make it so that if there are no 'tab' url param
  // that the url would add a default '?tab=publicModels'?
  const [_activeTab, setActiveTab] = useQueryParamOption({
    param: 'tab',
    options: tabTypes,
    validateOption: getTabType,
  });
  const activeTab: TabType = _activeTab ?? 'publicModels';

  const [_viewParam /* , setViewParam*/] = useQueryParamOption({
    param: 'view',
    options: viewTypes,
    validateOption: getViewType,
  });
  const viewParam: ViewType = _viewParam ?? 'list';

  const { hasProjectAccess, accountId } = useDbtCloudAccount();

  const publicTab = activeTab === 'publicModels';
  const projects = useProjects({ accountId });
  const jobs = useJobs({ accountId });

  const projectsWithJobRuns = jobs.data?.reduce((acc, job) => {
    if (job.most_recent_completed_run) {
      acc.push(job.project_id);
    }
    return acc;
  }, [] as number[]);
  const defaultEnvs = useAdminProductionEnvironments(
    accountId || 0,
    projects.data?.map((project) => project.id || 0) || [],
  );
  const envList = defaultEnvs.data?.map((env) => env.id!);
  const projectIdToEnvIdMap = useMemo(() => {
    return (
      defaultEnvs.data?.reduce(
        (acc, env) => {
          // Projects with no runs will fail auth in the resource count query
          if (projectsWithJobRuns?.includes(env.project_id!)) {
            acc[env.project_id!] = env.id!;
          }
          return acc;
        },
        {} as Record<number, number>,
      ) || {}
    );
  }, [defaultEnvs.data, projectsWithJobRuns]);
  const environmentSummaries = useEnvironmentSummaries({
    accountId,
    environmentIds: envList,
  }).data?.reduce(
    (acc, summary) => {
      acc[summary.environment_id] = summary;
      return acc;
    },
    {} as Record<number, EnvironmentSummary>,
  );
  const hydratedProjects = useMemo((): ProjectEntry[] => {
    if (devData.shouldMockProjects) {
      return devData.mockProjects.map((project) => ({
        dbtCloudProject: project.name,
        projectId: project.projectId || 0,
        defaultEnvironmentId: project.productionEnvironmentId || 0,
        dbtCoreProject: null,
        hasAccess: true,
      }));
    }
    if (!projects.data?.length) {
      return [];
    }

    if (!environmentSummaries) {
      return projects.data.map((project) => ({
        dbtCloudProject: project.name,
        projectId: project.id || 0,
        defaultEnvironmentId: projectIdToEnvIdMap[project.id || 0],
        dbtCoreProject: null,
        hasAccess: hasProjectAccess(project.id || 0),
      }));
    }

    return projects.data.map((project) => {
      const projectSummary = (environmentSummaries ?? {})[
        projectIdToEnvIdMap[project.id || 0]
      ];
      return {
        dbtCloudProject: project.name,
        projectId: project.id || 0,
        defaultEnvironmentId: projectIdToEnvIdMap[project.id || 0],
        dbtCoreProject: projectSummary?.dbt_project_name,
        hasAccess: hasProjectAccess(project.id || 0),
        projectSummary: {
          publicModels: projectSummary?.public_model,
          totalModels: projectSummary?.model,
          metrics: projectSummary?.metric,
        },
      } satisfies ProjectEntry;
    });
  }, [
    devData.shouldMockProjects,
    devData.mockProjects,
    projects.data,
    environmentSummaries,
    projectIdToEnvIdMap,
    hasProjectAccess,
  ])
    ?.filter(makeUniqueFilter((project) => project.projectId))
    .filter((project) => project.dbtCloudProject || project.dbtCoreProject);

  const publicModelsQuery = useAllPublicModels({
    accountId,
    includeDependentProjects: true,
    loadingStrategy: 'returnEachPage',
  });
  const publicModels = useMemo(
    (): SearchNode[] | undefined =>
      publicModelsQuery.data?.map((publicModel) => ({
        ...publicModel,
        hasAccess: !publicModel.projectId || hasProjectAccess(publicModel.projectId),
      })),
    [hasProjectAccess, publicModelsQuery.data],
  );

  const pageSize = INITIAL_PAGE_SIZE;
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedResourceParams, setSelectedResourceParams] =
    useState<ResourceByEnvironmentParams>({
      environmentId: undefined,
      uniqueId: undefined,
    });
  const [isColumnsPanelVisible, setIsColumnsPanelVisible] = useState(false);

  const nodes: (SearchNode | ProjectEntry)[] | undefined | null = publicTab
    ? publicModels
    : hydratedProjects;

  const resultQuery = useAccountLevelSearchResults<ProjectEntry | SearchNode>({
    query: search,
    nodes: nodes || [],
    pageSize: pageSize,
  });
  const isLoading = publicTab ? publicModelsQuery.fetching : projects.isFetching;

  const pageOptions: DropdownOption[] = [
    { label: '5 Rows', value: 5 },
    { label: '10 Rows', value: 10 },
    { label: '25 Rows', value: 25 },
    { label: '50 Rows', value: 50 },
  ];

  if (!accountId) return null;
  return (
    <Page title="Account Overview" pageType="multiProject">
      <ColumnsSidePanel
        isVisible={isColumnsPanelVisible}
        setIsVisible={setIsColumnsPanelVisible}
        selectedResourceParams={selectedResourceParams}
      />
      <div className="flex h-full w-full flex-col">
        {viewParam === 'lineage' && <MultiProjectLineageView />}
        {viewParam === 'list' && (
          <div className="mx-6 my-4">
            <div className="overflow-x-auto">
              <FloatingTabs>
                <FloatingTabs.Tab
                  text={'Public Models'}
                  id="publicModels"
                  isActive={activeTab === 'publicModels'}
                  onClick={() => setActiveTab('publicModels')}
                  count={publicModelsQuery.pageInfo?.totalCount ?? undefined}
                />
                <FloatingTabs.Tab
                  text={'Projects'}
                  id="projects"
                  isActive={activeTab === 'projects'}
                  onClick={() => setActiveTab('projects')}
                  count={hydratedProjects.length}
                />
              </FloatingTabs>
            </div>
            {activeTab === 'publicModels' && (
              <PublicModelsTable
                paginationType="loadMore"
                publicModels={resultQuery.data}
                pageOptions={pageOptions}
                pageSize={pageSize}
                isLoading={isLoading}
                hasMoreResults={resultQuery.hasMoreResults}
                fetchMore={resultQuery.fetchMore}
                setSelectedResourceParams={setSelectedResourceParams}
                setIsColumnsPanelVisible={setIsColumnsPanelVisible}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalCount={resultQuery.totalCount}
                totalPages={resultQuery.totalPages}
              />
            )}
            {activeTab === 'projects' && (
              <MeshProjectTable
                paginationType="loadMore"
                allProjects={resultQuery.data as ProjectEntry[]}
                pageOptions={pageOptions}
                pageSize={pageSize}
                isLoading={isLoading}
                hasMoreResults={resultQuery.hasMoreResults}
                fetchMore={resultQuery.fetchMore}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalCount={resultQuery.totalCount}
                totalPages={resultQuery.totalPages}
              />
            )}
          </div>
        )}
      </div>
    </Page>
  );
};
